import type {
  CentinelaCall,
  CentinelaFinding,
  CentinelaTranscriptLine,
} from "../../../features/centinela/types";

export const CENTINELA_CALL_OUTCOMES = [
  "confirmed",
  "answered",
  "noAnswer",
  "escalated",
  "duress",
] as const;

export type CentinelaCallOutcome = (typeof CENTINELA_CALL_OUTCOMES)[number];

export interface CentinelaTranscriptView {
  id: string;
  isSentinel: boolean;
  speaker: string | null;
  text: string;
  at: string;
}

export interface CentinelaCallView {
  id: string;
  caseId: string | null;
  callType: string;
  outcome: CentinelaCallOutcome;
  startedAt: string;
  durationSeconds: number;
  hasRecording: boolean;
  duressDetected: boolean;
  maskedPhone: string | null;
  findings: CentinelaFinding[];
  transcript: CentinelaTranscriptView[];
}

export const dayKeyOf = (iso: string): string => iso.slice(0, 10);

const ESCALATING_FINDINGS = new Set([
  "coaccion",
  "no_puede_hablar",
  "explicacion_inconsistente",
]);

const RESOLVING_FINDINGS = new Set(["todo_bien"]);

export function deriveCallOutcome(call: CentinelaCall): CentinelaCallOutcome {
  if (call.duress_detected) return "duress";
  if (!call.answered_at) return "noAnswer";

  const findings = (call.findings ?? []).map((finding) => finding.tipo);
  if (findings.some((tipo) => ESCALATING_FINDINGS.has(tipo))) return "escalated";
  if (findings.some((tipo) => RESOLVING_FINDINGS.has(tipo))) return "confirmed";
  return "answered";
}

const toTranscript = (
  lines: CentinelaTranscriptLine[],
  callId: string,
): CentinelaTranscriptView[] =>
  (lines ?? []).map((line, index) => ({
    id: `${callId}-${index}`,
    isSentinel: line.role === "ia",
    speaker: line.speaker?.trim() || null,
    text: line.text,
    at: line.at,
  }));

export function mapCentinelaCall(call: CentinelaCall): CentinelaCallView {
  return {
    id: call.id,
    caseId: call.case_id ?? null,
    callType: call.call_type,
    outcome: deriveCallOutcome(call),
    startedAt: call.started_at,
    durationSeconds: call.duration_sec ?? 0,
    hasRecording: call.has_recording === true,
    duressDetected: call.duress_detected === true,
    maskedPhone: call.to_phone_masked ?? null,
    findings: call.findings ?? [],
    transcript: toTranscript(call.transcript, call.id),
  };
}

export function mapCentinelaCalls(calls: CentinelaCall[] | undefined): CentinelaCallView[] {
  return (calls ?? [])
    .map(mapCentinelaCall)
    .sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

export function countCallsToday(calls: CentinelaCallView[], now = new Date()): number {
  const today = now.toISOString().slice(0, 10);
  return calls.filter((call) => call.startedAt.slice(0, 10) === today).length;
}
